/**
 * ParameterPreview Component
 *
 * Shows a compact preview of key parameters on workflow nodes.
 * Displayed below the node label in detailed zoom level.
 */

import { memo } from 'react';
import { makeStyles, tokens, mergeClasses, Tooltip } from '@coreai/fluentui-react';

export type ParameterPreviewProps = {
  /** Key-value pairs to display (max 3 shown) */
  parameters: Record<string, string | number | boolean | null | undefined>;
  /** Maximum number of parameters to show (default: 2) */
  maxItems?: number;
  /** Size variant */
  size?: 'small' | 'medium';
};

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    marginTop: tokens.spacingVerticalXS,
    maxWidth: '160px',
  },
  containerSmall: {
    maxWidth: '120px',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    fontSize: tokens.fontSizeBase100,
    lineHeight: '1.2',
  },
  key: {
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
    maxWidth: '50px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  value: {
    color: tokens.colorNeutralForeground1,
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: '9px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '100px',
    backgroundColor: tokens.colorNeutralBackground3,
    padding: '1px 4px',
    borderRadius: tokens.borderRadiusSmall,
  },
  moreIndicator: {
    color: tokens.colorNeutralForeground4,
    fontSize: '9px',
    fontStyle: 'italic',
  },
});

const formatValue = (value: string | number | boolean | null | undefined): string => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'number') return value.toString();
  // Truncate long strings
  const str = String(value);
  if (str.length > 20) return str.slice(0, 17) + '...';
  return str;
};

/**
 * Compact parameter preview for workflow nodes.
 * Shows key parameters in a minimal format.
 */
export const ParameterPreview = memo(({
  parameters,
  maxItems = 2,
  size = 'medium',
}: ParameterPreviewProps) => {
  const styles = useStyles();

  // Filter out empty/null values and get entries
  const entries = Object.entries(parameters)
    .filter(([, value]) => value !== null && value !== undefined && value !== '')
    .slice(0, maxItems + 1); // Get one extra to check if there are more

  if (entries.length === 0) return null;

  const visibleEntries = entries.slice(0, maxItems);
  const hasMore = entries.length > maxItems;
  const moreCount = Object.keys(parameters).length - maxItems;

  return (
    <div className={mergeClasses(styles.container, size === 'small' && styles.containerSmall)}>
      {visibleEntries.map(([key, value]) => (
        <Tooltip
          key={key}
          content={`${key}: ${String(value)}`}
          relationship="description"
          positioning="below"
        >
          <div className={styles.row}>
            <span className={styles.key}>{key}:</span>
            <span className={styles.value}>{formatValue(value)}</span>
          </div>
        </Tooltip>
      ))}
      {hasMore && (
        <span className={styles.moreIndicator}>+{moreCount} more</span>
      )}
    </div>
  );
});

ParameterPreview.displayName = 'ParameterPreview';
